"use client"

import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import {
  Video, Globe, Users, Sparkles, ArrowRight, Play, Shield, Zap, Heart,
  Sun, Moon, MessageCircle, Radio, ChevronDown, Star, Eye,
} from "lucide-react"
import PostWizard from "@/components/ui/PostWizard"
import { Plus } from "lucide-react"
import { useTheme } from "next-themes"
import { LanguageSwitcher } from "@/components/i18n/language-switcher"
import { useLanguage } from "@/components/i18n/language-provider"

export default function HomePage() {
  const [showWizard, setShowWizard] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const { theme, setTheme } = useTheme()
  const { t } = useLanguage()
  const tickerItems = [
    t("landingTickerRandomVideo"),
    t("landingTickerAltOmegle"),
    t("landingTickerLives"),
    t("landingTickerMessaging"),
    t("landingTickerFree"),
    t("landingTickerUsers"),
    t("landingTickerNoDownload"),
    t("landingTickerAlwaysOn"),
  ]
  const [mounted, setMounted] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [videoReady, setVideoReady] = useState(false)
  const [shouldLoadVideo, setShouldLoadVideo] = useState(true)

  // Pause other background videos when this one plays
  const pauseOtherBackgroundVideos = (self: HTMLVideoElement | null) => {
    if (!self) return
    const others = Array.from(document.querySelectorAll('video[data-bg]')) as HTMLVideoElement[]
    others.forEach((v) => {
      if (v !== self) {
        try {
          v.pause()
        } catch {}
      }
    })
  }

  useEffect(() => { setMounted(true) }, [])

  // Chargement immédiat de la vidéo (pas de délai)

  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    let settled = false

    // Improve autoplay reliability on mobile/tablet by forcing inline-muted playback flags.
    v.muted = true
    v.defaultMuted = true
    v.playsInline = true
    v.setAttribute("muted", "")
    v.setAttribute("autoplay", "")
    v.setAttribute("loop", "")
    v.setAttribute("playsinline", "")
    v.setAttribute("webkit-playsinline", "true")

    const ensureAutoplay = () => {
      v.muted = true
      v.defaultMuted = true
      v.autoplay = true
      v.playsInline = true
      v.loop = true
      v.setAttribute("muted", "")
      v.setAttribute("autoplay", "")
      v.setAttribute("playsinline", "")
      v.setAttribute("webkit-playsinline", "true")
      if (v.paused) {
        v.play().catch(() => {})
      }
    }

    // Load once, then only retry play (avoid repeated load() loops that can freeze UI on mobile).
    v.load()

    // Initial play attempt
    ensureAutoplay()

    // Extra early retries for first entry on mobile/tablet.
    const t1 = window.setTimeout(ensureAutoplay, 60)
    const t2 = window.setTimeout(ensureAutoplay, 180)
    const t3 = window.setTimeout(ensureAutoplay, 420)
    const t4 = window.setTimeout(ensureAutoplay, 900)
    const t5 = window.setTimeout(ensureAutoplay, 1800)

    // Keep retrying for longer on first load (mobile/tablet can be slower to unlock media pipeline).
    const retryTimer = window.setInterval(() => {
      if (!settled && v.paused) ensureAutoplay()
    }, 500)
    const stopRetryTimer = window.setTimeout(() => window.clearInterval(retryTimer), 30000)

    const onCanPlay = () => ensureAutoplay()
    const onCanPlayThrough = () => ensureAutoplay()
    const onLoadedMetadata = () => ensureAutoplay()
    const onLoadedData = () => ensureAutoplay()
    const onPause = () => {
      if (!settled) ensureAutoplay()
    }
    const onPlay = () => {
      settled = true
      window.clearInterval(retryTimer)
      window.clearTimeout(stopRetryTimer)
    }
    v.addEventListener("canplay", onCanPlay)
    v.addEventListener("canplaythrough", onCanPlayThrough)
    v.addEventListener("loadedmetadata", onLoadedMetadata)
    v.addEventListener("loadeddata", onLoadedData)
    v.addEventListener("pause", onPause)
    v.addEventListener("play", onPlay)

    // Resume playback when tab becomes visible again
    const onVisibility = () => {
      if (document.visibilityState === "visible" && v.paused) {
        ensureAutoplay()
      }
    }
    const onPageShow = () => ensureAutoplay()
    const onFocus = () => ensureAutoplay()
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("pageshow", onPageShow)
    window.addEventListener("focus", onFocus)

    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.clearTimeout(t3)
      window.clearTimeout(t4)
      window.clearTimeout(t5)
      window.clearInterval(retryTimer)
      window.clearTimeout(stopRetryTimer)
      v.removeEventListener("canplay", onCanPlay)
      v.removeEventListener("canplaythrough", onCanPlayThrough)
      v.removeEventListener("loadedmetadata", onLoadedMetadata)
      v.removeEventListener("loadeddata", onLoadedData)
      v.removeEventListener("pause", onPause)
      v.removeEventListener("play", onPlay)
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("pageshow", onPageShow)
      window.removeEventListener("focus", onFocus)
    }
  }, [])

  useEffect(() => {
    let m = true
    ;(async () => {
      try {
        const r = await fetch("/api/users/me")
        if (!m) return
        if (r.ok) {
          setIsAuthenticated(true)
          // Final check: if we are on the landing page but authenticated, go to dashboard
          if (window.location.pathname === "/") {
            window.location.href = "/dashboard"
          }
        } else {
          setIsAuthenticated(false)
        }
      } catch {
        if (m) setIsAuthenticated(false)
      }
    })()
    return () => { m = false }
  }, [])

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* ── Theme toggle ── */}
      {mounted && (
        <div className="fixed top-5 right-5 z-50 flex items-center gap-3">
          <LanguageSwitcher />
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="p-2.5 rounded-full bg-white/90 dark:bg-white/10 backdrop-blur-lg border border-gray-200 dark:border-white/20 shadow-lg hover:scale-110 transition-transform"
            aria-label={t("theme")}
          >
            {theme === "dark" ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-primary" />}
          </button>
        </div>
      )}

      <main>
      {/* ═══════════════════════════════════════════════
          HERO — Full-screen video background
          ═══════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#0b1020' }}>
        {/* Video background */}
        <div className="absolute inset-0">
          {shouldLoadVideo ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              data-bg="true"
              onCanPlay={() => setVideoReady(true)}
              onLoadedData={() => setVideoReady(true)}
              onPlay={(e) => {
                setVideoReady(true)
                pauseOtherBackgroundVideos(e.currentTarget as HTMLVideoElement)
              }}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "brightness(0.65)", backgroundColor: '#050816' }}
            >
              {/* Serve a small/SD source first so the background paints quickly, then the HD source */}
              <source src="/videos/auth-bg-sd.mp4" type="video/mp4" />
              <source src="/videos/auth-bg.mp4" type="video/mp4" />
            </video>
          ) : null}
            {/* Overlay adaptatif dark/light — rendered only until the video is ready */}
            {!videoReady && (
              <div className="absolute inset-0 bg-black/20 dark:bg-black/40 pointer-events-none" aria-hidden />
            )}
          {/* Grain subtil */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIgb3BhY2l0eT0iLjA1Ii8+PC9zdmc+')] opacity-40" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto text-center px-4 py-20">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-sm font-medium mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            {t("heroBadge")}
          </div>

          {/* Logo */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <img src="/logo.svg?v=20260409" alt="Callo" onError={(e) => { e.currentTarget.src = "/icon-192x192.png" }} className="w-16 h-16 md:w-20 md:h-20 rounded-2xl shadow-2xl" loading="eager" decoding="sync" />
            <h1 className="text-6xl md:text-8xl lg:text-9xl font-black text-white tracking-tighter drop-shadow-2xl">
              Callo
            </h1>
          </div>

          {/* SEO-rich headline */}
          <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-white/95 max-w-4xl mx-auto mb-4 leading-tight drop-shadow-lg">
            {t("heroTitle")}
          </h2>
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t("heroSubtitle")}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            {isAuthenticated ? (
              <Button
                size="lg"
                onClick={() => setShowWizard(true)}
                className="animated-gradient text-white border-0 px-10 py-7 text-lg rounded-full shadow-2xl hover:shadow-indigo-500/25 hover:scale-105 transition-all duration-300"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t("ctaPublish")}
              </Button>
            ) : (
              <>
                <Button
                  size="lg"
                  asChild
                  className="animated-gradient text-white border-0 px-10 py-7 text-lg rounded-full shadow-2xl hover:shadow-indigo-500/25 hover:scale-105 transition-all duration-300"
                >
                  <Link href="/auth/register">
                    {t("ctaRegister")}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="bg-transparent px-10 py-7 text-lg rounded-full border-2 border-white/30 text-white hover:bg-white/10 hover:text-white hover:border-white/50 transition-all duration-300 backdrop-blur-sm"
                >
                  <Link href="/auth/login">{t("ctaLogin")}</Link>
                </Button>
              </>
            )}
          </div>

          {/* Stats pills */}
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {[
              { value: "100%", label: t("landingStatFree"), icon: Star },
              { value: "1K+", label: t("landingStatUsers"), icon: Users },
              { value: "18+", label: t("landingStatCountries"), icon: Globe },
              { value: "24/7", label: t("landingStatAvailable"), icon: Zap },
            ].map((stat) => (
              <div
                key={stat.label}
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15"
              >
                <stat.icon className="w-4 h-4 text-white/60" />
                <span className="text-white font-bold text-sm">{stat.value}</span>
                <span className="text-white/50 text-xs">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-6 h-6 text-white/40" />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SEO TICKER — scrolling keywords
          ═══════════════════════════════════════════════ */}
      <div className="relative py-4 bg-primary/5 dark:bg-primary/10 border-y border-border/30 overflow-hidden">
        <div className="landing-ticker flex gap-8 whitespace-nowrap">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className="text-sm font-medium text-muted-foreground/70 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/40" />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════
          HOW IT WORKS — 3 steps
          ═══════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-36 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
              {t("landingHowBadge")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t("landingHowTitle")} <span className="gradient-text">{t("landingHowTitleAccent")}</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              {t("landingHowSubtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              { step: "01", icon: Users, title: t("landingHowStep1Title"), desc: t("landingHowStep1Desc"), color: "from-indigo-500 to-indigo-700" },
              { step: "02", icon: Video, title: t("landingHowStep2Title"), desc: t("landingHowStep2Desc"), color: "from-blue-500 to-blue-700" },
              { step: "03", icon: Heart, title: t("landingHowStep3Title"), desc: t("landingHowStep3Desc"), color: "from-cyan-500 to-teal-600" },
            ].map((item) => (
              <div key={item.step} className="relative group">
                <div className="text-7xl md:text-8xl font-black text-primary/5 dark:text-primary/10 absolute -top-4 -left-2 select-none">
                  {item.step}
                </div>
                <div className="relative pt-10">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 group-hover:shadow-xl transition-all duration-300`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          FEATURES — Rich cards
          ═══════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-36 px-4 bg-muted/30 dark:bg-muted/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
              {t("landingFeaturesBadge")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t("landingFeaturesTitle")} <span className="gradient-text">{t("landingFeaturesTitleAccent")}</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t("landingFeaturesSubtitle")}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Globe, title: t("landingFeatureGlobalTitle"), desc: t("landingFeatureGlobalDesc"), color: "from-indigo-500 to-indigo-700", tag: t("landingTagPopular") },
              { icon: Zap, title: t("landingFeatureInstantTitle"), desc: t("landingFeatureInstantDesc"), color: "from-cyan-500 to-teal-600", tag: null },
              { icon: Radio, title: t("landingFeatureLivesTitle"), desc: t("landingFeatureLivesDesc"), color: "from-blue-500 to-blue-700", tag: t("landingTagNew") },
              { icon: MessageCircle, title: t("landingFeatureMessagingTitle"), desc: t("landingFeatureMessagingDesc"), color: "from-emerald-500 to-teal-600", tag: null },
              { icon: Users, title: t("landingFeatureCommunityTitle"), desc: t("landingFeatureCommunityDesc"), color: "from-sky-500 to-blue-600", tag: null },
              { icon: Shield, title: t("landingFeatureSecurityTitle"), desc: t("landingFeatureSecurityDesc"), color: "from-slate-500 to-gray-600", tag: null },
            ].map((feature) => (
              <div
                key={feature.title}
                className="group relative bg-card rounded-2xl p-6 md:p-8 border border-border/50 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 cursor-default"
              >
                {feature.tag && (
                  <span className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider">
                    {feature.tag}
                  </span>
                )}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SOCIAL PROOF — Testimonials
          ═══════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-36 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
              {t("landingTestimonialsBadge")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t("landingTestimonialsTitle")} <span className="gradient-text">Callo</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: t("landingTestimonial1Name"), flag: "🇫🇷", text: t("landingTestimonial1Text"), rating: 5 },
              { name: t("landingTestimonial2Name"), flag: "🇫🇷", text: t("landingTestimonial2Text"), rating: 5 },
              { name: t("landingTestimonial3Name"), flag: "🇪🇸", text: t("landingTestimonial3Text"), rating: 5 },
            ].map((item) => (
              <div key={item.name} className="bg-card rounded-2xl p-6 border border-border/50 hover:border-primary/20 transition-colors">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">&ldquo;{item.text}&rdquo;</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full animated-gradient flex items-center justify-center text-white text-xs font-bold">
                    {item.name[0]}
                  </div>
                  <span className="text-sm font-medium">{item.name}</span>
                  <span className="text-sm">{item.flag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          FINAL CTA — Gradient card
          ═══════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-36 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="relative animated-gradient rounded-3xl p-10 md:p-20 text-center overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-black/10" />
            <div className="relative z-10">
              <Sparkles className="w-12 h-12 md:w-14 md:h-14 text-white/90 mx-auto mb-6" />
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
                {t("landingCtaTitleLine1")}
                <br />
                {t("landingCtaTitleLine2")}
              </h2>
              <p className="text-white/75 text-lg md:text-xl mb-10 max-w-xl mx-auto">
                {t("landingCtaSubtitle")}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  asChild
                  className="bg-white text-gray-900 hover:bg-white/90 px-10 py-7 text-lg rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 font-bold"
                >
                  <Link href="/auth/register">
                    {t("landingCtaPrimary")}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  asChild
                  className="bg-transparent text-white hover:text-white hover:bg-white/10 px-10 py-7 text-lg rounded-full border-2 border-white/30 transition-all duration-300"
                >
                  <Link href="/auth/login">{t("landingCtaSecondary")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      </main>

      {/* ═══════════════════════════════════════════════
          SEO FOOTER — Rich with keywords
          ═══════════════════════════════════════════════ */}
      <footer className="py-12 px-4 border-t border-border/50 bg-muted/20 dark:bg-muted/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-8">
            <div className="max-w-sm">
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo.svg?v=20260409" alt="Callo" onError={(e) => { e.currentTarget.src = "/icon-192x192.png" }} className="w-7 h-7 rounded-lg" loading="eager" decoding="sync" />
                <span className="text-xl font-black gradient-text">Callo</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("landingFooterDesc")}
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-12 gap-y-2 text-sm">
              <Link href="/a-propos" className="text-muted-foreground hover:text-foreground transition-colors">{t("publicNavAbout")}</Link>
              <Link href="/contact" className="text-muted-foreground hover:text-foreground transition-colors">{t("publicNavContact")}</Link>
              <Link href="/securite" className="text-muted-foreground hover:text-foreground transition-colors">{t("publicNavSafety")}</Link>
              <Link href="/auth/register" className="text-muted-foreground hover:text-foreground transition-colors">{t("landingFooterRegister")}</Link>
              <Link href="/auth/login" className="text-muted-foreground hover:text-foreground transition-colors">{t("landingFooterLogin")}</Link>
              <Link href="/discover" className="text-muted-foreground hover:text-foreground transition-colors">{t("landingFooterDiscover")}</Link>
              <Link href="/live" className="text-muted-foreground hover:text-foreground transition-colors">{t("landingFooterLive")}</Link>
              <Link href="/legal/conditions-utilisation" className="text-muted-foreground hover:text-foreground transition-colors">{t("landingFooterTerms")}</Link>
              <Link href="/legal/politique-confidentialite" className="text-muted-foreground hover:text-foreground transition-colors">{t("landingFooterPrivacy")}</Link>
              <Link href="/legal/mentions-legales" className="text-muted-foreground hover:text-foreground transition-colors">{t("landingFooterLegal")}</Link>
            </div>
          </div>
          <div className="border-t border-border/30 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              {t("landingCopyright").replace("{year}", String(new Date().getFullYear()))}
            </p>
            <p className="text-xs text-muted-foreground/50">
              {t("landingLegalPrefix")} <Link href="/legal/conditions-utilisation" className="underline hover:text-foreground transition-colors">{t("landingFooterTerms")}</Link> {t("landingLegalAnd")} <Link href="/legal/politique-confidentialite" className="underline hover:text-foreground transition-colors">{t("landingFooterPrivacy")}</Link>.
            </p>
          </div>
        </div>
      </footer>

      {/* Post Wizard */}
      {showWizard && isAuthenticated && <PostWizard onClose={() => setShowWizard(false)} onPosted={() => { setShowWizard(false) }} />}
    </div>
  )
}
